import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { Event, NavigationStart, Router } from '@angular/router';
import { Observable, combineLatest, of } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { AppFacade } from 'ish-core/facades/app.facade';
import { DeviceType, HeaderType, headerTypes } from 'ish-core/models/viewtype/viewtype.types';

/**
 * CUSTOMIZATION: Pin default storefront chrome immediately (no scroll-away → snap).
 * Checkout / simple / error headers stay in normal document flow so content is not
 * covered by a fixed bar (height spacer is `auto` for typed headers).
 */
@Component({
  selector: 'custom-header',
  templateUrl: './custom-header.component.html',
  styleUrls: ['./custom-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CustomHeaderComponent implements OnInit {
  headerType$: Observable<string>;
  deviceType$: Observable<DeviceType>;
  isSticky$: Observable<boolean>;
  reset$: Observable<Event>;
  wrapperClasses$: Observable<string[]>;
  headerHeight$: Observable<number>;
  animationWrapperClasses$: Observable<string[]>;
  customHeaderWrapperClasses$: Observable<string[]>;
  headerClasses$: Observable<string[]>;
  desktopHeaderHeight$: Observable<string>;

  constructor(private appFacade: AppFacade, private router: Router) {}

  ngOnInit() {
    this.headerType$ = SSR
      ? of(undefined)
      : this.appFacade.headerType$.pipe(
          map((headerType: HeaderType) => (headerTypes.includes(headerType) ? headerType : undefined))
        );
    this.deviceType$ = this.appFacade.deviceType$;
    this.headerHeight$ = this.appFacade.getDesktopHeaderHeight$;
    // Sticky only for default storefront header — not checkout/simple/error.
    this.isSticky$ = this.headerType$.pipe(map(headerType => !headerType));
    this.customHeaderWrapperClasses$ = this.headerType$.pipe(
      map(headerType => (headerType ? ['custom-relative-header'] : ['custom-sticky-header']))
    );
    this.animationWrapperClasses$ = of([]);
    this.reset$ = this.router.events.pipe(filter(event => event instanceof NavigationStart));

    this.headerClasses$ = combineLatest([this.customHeaderWrapperClasses$, this.animationWrapperClasses$]).pipe(
      map(([customClasses, animationClasses]) => [...customClasses, ...animationClasses])
    );

    this.desktopHeaderHeight$ = combineLatest([this.deviceType$, this.headerHeight$, this.headerType$]).pipe(
      map(([deviceType, headerHeight, headerType]) => {
        if (headerType) {
          return 'auto';
        }
        if (deviceType === 'desktop' && headerHeight !== 0) {
          return `${headerHeight}px`;
        }
        return 'auto';
      })
    );
  }
}
